import React from 'react'
import MesCommandesItemThumb from '../mesCommandes/MesCommandesItemThumb'
import MesCommandesDetailSection from '../mesCommandes/MesCommandesDetailSection'
import { buildRdvPricingRecap } from '../../utils/priceTax'
import { getRdvPaymentView, formatEuro } from '../../utils/rdvPaymentDisplay'
import { resolveRdvPaymentTone, rdvPaymentToneClassName } from '../../utils/rdvPaymentTone'
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay'
import { resolvePrestationThumbUrl } from '../../utils/mesCommandesMixedUx'
import {
  formatRdvAppointmentBlock,
  formatRdvPaymentStatusLabel,
  formatRdvReservedAtLabel,
  formatInstructeurDisplay,
  formatMesCommandesRdvDateCompact,
} from '../../utils/mesCommandesRdvDates'
import {
  getRdvDetailStatusUx,
  getRdvNumeroDisplay,
  getRdvNumeroShortDisplay,
} from '../../utils/rdvStatusUx'

function formatDurationLabel(minutes) {
  const n = Number(minutes)
  if (!Number.isFinite(n) || n <= 0) return null
  return `${n} min`
}

function formatPeopleLabel(count) {
  const n = Math.max(1, Number(count) || 1)
  return `${n} personne${n > 1 ? 's' : ''}`
}

function hasClientInfo(user) {
  if (!user) return false
  return Boolean(
    (user.prenom && String(user.prenom).trim()) ||
      (user.nom && String(user.nom).trim()) ||
      (user.email && String(user.email).trim()) ||
      (user.telephone && String(user.telephone).trim()),
  )
}

function DetailBadge({ label, className }) {
  if (!label) return null
  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full ${className}`}
    >
      {label}
    </span>
  )
}


/**
 * Corps UX du modal détail RDV Admin — aligné sur le modal commande produit (Mes commandes).
 */
export default function RdvDetailContent({
  commande,
  statusDisplay,
  footer = null,
  showCommercant = true,
}) {
  if (!commande) return null

  const recap = buildRdvPricingRecap(commande)
  const {
    participants: participantsCount,
    prestation,
    optionsDetails,
    totalHT,
    totalTVA,
    montantAPayer,
    promoReduction,
    defaultTvaRate,
    unitTTC,
  } = recap

  const rdvPay = getRdvPaymentView(commande)
  const paymentTone = resolveRdvPaymentTone(commande)
  const statusUx = getRdvDetailStatusUx(commande, statusDisplay)
  const paymentStatusLabel = formatRdvPaymentStatusLabel(commande)
  const numeroFull = getRdvNumeroDisplay(commande)
  const numeroShort = getRdvNumeroShortDisplay(commande)
  const durationLabel = formatDurationLabel(commande.prestation?.dureeMinutes ?? 60)
  const peopleLabel = formatPeopleLabel(participantsCount)
  const prestationNom = commande.prestation?.nom?.trim() || 'Prestation'
  const clientUser = commande.user
  const showClient = hasClientInfo(clientUser)
  const appointment = formatRdvAppointmentBlock(commande.dateDebut, commande.dateFin)
  const reservedLabel = formatRdvReservedAtLabel(commande)
  const prestationImageUrl = resolvePrestationThumbUrl(commande)
  const instructeurLabel = formatInstructeurDisplay(commande.instructeur, commande.instructeurId)
  const isAnnule = String(commande.statut || '').toUpperCase() === 'ANNULE'
  const amountDueLater = Number(rdvPay?.amountDueLater ?? 0)
  const amountPaidDisplay = Number(rdvPay?.amountPaid ?? 0)
  const showAmountDue = Boolean(rdvPay && !rdvPay.isRefunded && amountDueLater > 0.009)
  const showFullyPaidHighlight = Boolean(
    rdvPay && !rdvPay.isRefunded && !showAmountDue && amountPaidDisplay > 0.009,
  )

  return (
    <div className="space-y-6">
      {/* 1 — En-tête / référence / badges */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">Référence</p>
          <p
            className="text-xl font-bold text-gray-900 tracking-tight"
            title={numeroFull}
          >
            {numeroShort}
          </p>
          {numeroFull && numeroFull !== numeroShort ? (
            <p
              className="mt-0.5 text-[11px] font-mono text-gray-400 truncate max-w-[16rem] sm:max-w-md"
              title={numeroFull}
            >
              {numeroFull}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <DetailBadge label={statusUx.label} className={statusUx.color} />
          <DetailBadge
            label={`Paiement : ${paymentStatusLabel}`}
            className={rdvPaymentToneClassName('badge', paymentTone)}
          />
        </div>
      </div>

      {/* 2 — Prestation */}
      <MesCommandesDetailSection title="Prestation">
        <div className="flex items-start gap-4">
          <MesCommandesItemThumb
            variant="rdv"
            size="maquette"
            imageUrl={prestationImageUrl}
            alt={prestationNom}
          />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 text-base">{prestationNom}</p>
            <dl className="mt-2 space-y-1 text-sm text-gray-700">
              {durationLabel ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">Durée</dt>
                  <dd className="font-medium">{durationLabel}</dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Participants</dt>
                <dd className="font-medium">{peopleLabel}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Prix</dt>
                <dd className="font-bold text-gray-900">{formatEuro(montantAPayer)} TTC</dd>
              </div>
            </dl>
          </div>
        </div>
      </MesCommandesDetailSection>

      {/* 3 — Client + Commerçant */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <MesCommandesDetailSection title="👤 Client">
          {showClient ? (
            <>
              <p className="font-medium text-gray-900">
                {[clientUser.prenom, clientUser.nom].filter(Boolean).join(' ') || '—'}
              </p>
              {clientUser.email ? (
                <p className="text-sm text-gray-600 break-all mt-1">{clientUser.email}</p>
              ) : null}
              {clientUser.telephone ? (
                <p className="text-sm text-gray-600 tabular-nums mt-0.5">
                  {formatPhoneDisplay(clientUser.telephone)}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-gray-500">Informations client indisponibles.</p>
          )}
        </MesCommandesDetailSection>

        {showCommercant && commande.commercant ? (
          <MesCommandesDetailSection title="🏪 Commerçant">
            <p className="font-medium">{commande.commercant?.nom}</p>
            {commande.commercant?.rue ? (
              <p className="text-sm text-gray-600 mt-1">{commande.commercant.rue}</p>
            ) : null}
            <p className="text-sm text-gray-600">
              {[commande.commercant?.codePostal, commande.commercant?.ville].filter(Boolean).join(' ')}
            </p>
          </MesCommandesDetailSection>
        ) : null}
      </div>

      {/* 4 — Dates */}
      <MesCommandesDetailSection title="Dates" tone="accent">
        <dl>
          <div>
            <dt className="text-xs font-bold uppercase tracking-wide text-purple-800">Rendez-vous</dt>
            <dd className="mt-2 text-lg sm:text-xl font-bold text-gray-900 leading-snug">
              {appointment.dateLine}
            </dd>
            {appointment.timeLine ? (
              <dd className="mt-1.5 text-base font-semibold text-gray-800 tabular-nums">
                {appointment.timeLine}
              </dd>
            ) : null}
          </div>
          <div className="mt-4 border-t border-gray-100 pt-3">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Réservé le</dt>
            <dd className="mt-0.5 text-sm text-gray-600">{reservedLabel}</dd>
          </div>
        </dl>
      </MesCommandesDetailSection>

      {(instructeurLabel ||
        (commande.notes && String(commande.notes).trim()) ||
        (commande.notesCommercant && String(commande.notesCommercant).trim())) && (
        <MesCommandesDetailSection title="Informations RDV">
          {instructeurLabel ? (
            <p className="text-sm mb-2">
              <span className="text-gray-500">Professionnel / instructeur : </span>
              <span className="font-medium text-gray-900">{instructeurLabel}</span>
            </p>
          ) : null}
          {commande.notes && String(commande.notes).trim() ? (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Note du client</p>
              <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                {String(commande.notes).trim()}
              </p>
            </div>
          ) : null}
          {commande.notesCommercant && String(commande.notesCommercant).trim() ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Note commerçant</p>
              <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                {String(commande.notesCommercant).trim()}
              </p>
            </div>
          ) : null}
        </MesCommandesDetailSection>
      )}

      {isAnnule && (commande.dateAnnulation || commande.raisonAnnulation || commande.annulePar) ? (
        <MesCommandesDetailSection title="Annulation">
          {commande.dateAnnulation ? (
            <p className="text-sm">
              Date :{' '}
              <span className="font-medium">
                {formatMesCommandesRdvDateCompact(commande.dateAnnulation) || '—'}
              </span>
            </p>
          ) : null}
          {commande.annulePar ? (
            <p className="text-sm mt-1">
              Par : <span className="font-medium">{commande.annulePar}</span>
            </p>
          ) : null}
          {commande.raisonAnnulation ? (
            <p className="text-sm mt-2 whitespace-pre-wrap text-gray-700">{commande.raisonAnnulation}</p>
          ) : null}
        </MesCommandesDetailSection>
      ) : null}

      {/* 5 — Paiement */}
      <MesCommandesDetailSection title="💳 Paiement">
        {rdvPay ? (
          <div className="space-y-4">
            <DetailBadge
              label={paymentStatusLabel}
              className={rdvPaymentToneClassName('badge', paymentTone)}
            />

            {rdvPay.isRefunded ? (
              <p className="text-sm font-medium text-amber-800">Remboursement enregistré</p>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showAmountDue ? (
                <div
                  className={`sm:col-span-2 rounded-lg border p-4 ${rdvPaymentToneClassName('panel', paymentTone === 'danger' ? 'danger' : 'warning')}`}
                >
                  <p
                    className={`text-xs font-bold uppercase tracking-wide ${rdvPaymentToneClassName('panelTitle', paymentTone === 'danger' ? 'danger' : 'warning')}`}
                  >
                    Reste à payer
                  </p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                    {formatEuro(amountDueLater)}
                  </p>
                </div>
              ) : null}

              {showFullyPaidHighlight ? (
                <div
                  className={`sm:col-span-2 rounded-lg border p-4 ${rdvPaymentToneClassName('panel', 'success')}`}
                >
                  <p
                    className={`text-xs font-bold uppercase tracking-wide ${rdvPaymentToneClassName('panelTitle', 'success')}`}
                  >
                    Payé intégralement
                  </p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                    {formatEuro(amountPaidDisplay)}
                  </p>
                </div>
              ) : null}

              {!showAmountDue && !showFullyPaidHighlight ? (
                <div className="sm:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Payé</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                    {formatEuro(amountPaidDisplay)}
                  </p>
                </div>
              ) : null}

              {showAmountDue ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-medium text-gray-500">Payé</p>
                  <p className="mt-0.5 text-lg font-semibold text-gray-900 tabular-nums">
                    {formatEuro(amountPaidDisplay)}
                  </p>
                </div>
              ) : null}

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">Mode</p>
                <p className="mt-0.5 text-sm font-semibold text-gray-900">
                  {rdvPay.modeLabel}
                  {rdvPay.isOnline ? (
                    <span className="ml-1.5 text-[#635BFF] font-medium">Stripe</span>
                  ) : null}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">Prix prestation</p>
                <p className="mt-0.5 text-sm font-semibold text-gray-900 tabular-nums">
                  {formatEuro(montantAPayer)}
                </p>
              </div>
            </div>

            {promoReduction > 0 ? (
              <p className="text-sm text-green-700">
                Remise promo : <span className="font-semibold">-{formatEuro(promoReduction)}</span>
                {commande.codePromo ? (
                  <span className="ml-2 font-mono text-xs">({commande.codePromo})</span>
                ) : null}
              </p>
            ) : commande.codePromo ? (
              <p className="text-sm text-gray-600">
                Code promo : <span className="font-mono font-medium">{commande.codePromo}</span>
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Détail paiement indisponible.</p>
        )}
      </MesCommandesDetailSection>

      {/* 6 — Récapitulatif tarifaire */}
      <div className="border-l-4 border-purple-500 bg-white border border-gray-200 rounded-lg p-4 shadow-sm sm:p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-800">Récapitulatif tarifaire</h3>

        <div className="mb-4 space-y-3">
          <div className="border-b border-gray-100 py-2">
            <div className="mb-1 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700">{prestationNom}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {participantsCount} × {unitTTC.toFixed(2)}€ TTC
                  <span className="text-gray-400"> (TVA {defaultTvaRate}%)</span>
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold text-gray-900">{prestation.ttc.toFixed(2)}€ TTC</p>
                <p className="text-xs text-gray-500">
                  dont {prestation.ht.toFixed(2)}€ HT + {prestation.tva.toFixed(2)}€ TVA
                </p>
              </div>
            </div>
          </div>

          {optionsDetails.map((option) => (
            <div key={option.optionId || option.id} className="border-b border-gray-100 py-2">
              <div className="mb-1 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-700">
                    {option.nom || option.choixNom || 'Option'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {option.quantite} × {option.prixUnitaireTTC.toFixed(2)}€ TTC
                    <span className="text-gray-400"> (TVA {option.tauxTVA}%)</span>
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-gray-900">{option.ttc.toFixed(2)}€ TTC</p>
                  <p className="text-xs text-gray-500">
                    dont {option.ht.toFixed(2)}€ HT + {option.tva.toFixed(2)}€ TVA
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2 border-t-2 border-gray-300 pt-3">
          <p className="pb-1 text-xs text-gray-500">
            Prix client en TTC — détail comptable selon le taux TVA de la prestation.
          </p>
          <div className="flex justify-between text-sm text-gray-500">
            <span>Dont HT</span>
            <span>{totalHT.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-sm text-gray-500">
            <span>Dont TVA</span>
            <span>{totalTVA.toFixed(2)}€</span>
          </div>
          {promoReduction > 0 && (
            <div className="flex justify-between text-sm text-green-700">
              <span>Réduction (code promo)</span>
              <span className="font-medium">-{promoReduction.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-200 pt-2">
            <p className="text-base font-bold text-gray-900">
              {promoReduction > 0 ? 'Total à payer TTC' : 'Total TTC'}
            </p>
            <p className="text-lg font-bold text-gray-900">{montantAPayer.toFixed(2)}€</p>
          </div>
        </div>
      </div>

      {/* 7 — Actions */}
      {footer}
    </div>
  )
}

export { formatDurationLabel, formatPeopleLabel, hasClientInfo }
